import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import clsx from 'clsx';
import { TypedMemo } from '../../../common/types';
import { HealthBadge } from '../components/HealthBadge';
import type { MailboxRow } from '../outreachTypes';
import { nameInitials } from '../outreachFormat';
import { sendFromMessages as messages } from './sendFromMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for Outreach; each part uses its own slice of it */
import shared from '../Outreach.module.scss';
import styles from './Builder.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** A paused or reconnecting mailbox shows but cannot send (design outreach-v2 e2 note). */
export function canSendFrom(health: string): boolean {
  return health === 'ok' || health === 'warming';
}

interface MailboxPickProps {
  mailboxes: MailboxRow[];
  picked: ReadonlySet<string>;
  song: string;
  readOnly: boolean;
  onToggle: (mailboxUuid: string) => void;
}

/** The mailboxes that the artist may send from, one row each (design outreach-v2 e2 `mbx`). */
const MailboxPickComponent: React.FC<MailboxPickProps> = ({ mailboxes, picked, song, readOnly, onToggle }) => {
  const intl = useIntl();
  const rows = useMemo(
    () =>
      mailboxes.map((mailbox) => {
        const usable = canSendFrom(mailbox.health);
        const on = usable && picked.has(mailbox.mailboxUuid);
        const state = () => {
          if (mailbox.health === 'paused') {
            return intl.formatMessage(messages.rowPaused);
          }
          if (mailbox.health === 'reconnect') {
            return intl.formatMessage(messages.rowReconnect);
          }
          return mailbox.warmup.warming
            ? intl.formatMessage(messages.rowWarming, { day: mailbox.warmup.day + 1, cap: mailbox.warmup.todayCap })
            : intl.formatMessage(messages.rowReady, { cap: mailbox.capToday });
        };
        const addedBy =
          mailbox.createdBy === null
            ? null
            : mailbox.createdBy.isYou
              ? intl.formatMessage(messages.addedByYou)
              : intl.formatMessage(messages.addedBy, { name: mailbox.createdBy.name });
        return (
          <button
            key={mailbox.mailboxUuid}
            type="button"
            className={clsx(styles.pickable, styles.mailbox, on && styles.picked, !usable && styles.mailboxOff)}
            disabled={!usable || readOnly}
            aria-pressed={on}
            aria-label={intl.formatMessage(messages.pick, { song, address: mailbox.address })}
            onClick={() => onToggle(mailbox.mailboxUuid)}
            data-testid="builder-mailbox"
          >
            <span className={styles.mailboxAvatar}>{nameInitials(mailbox.displayName || mailbox.address)}</span>
            <span className={shared.grow}>
              <b className="text-truncate">{mailbox.address}</b>
              <small>
                {[mailbox.displayName, state(), addedBy].filter((part) => part !== null && part !== '').join(' · ')}
              </small>
            </span>
            <HealthBadge health={mailbox.health} />
            <span className={clsx(shared.check, on && shared.checkOn)}>{on && <FontAwesomeIcon icon={faCheck} />}</span>
          </button>
        );
      }),
    [intl, mailboxes, onToggle, picked, readOnly, song]
  );
  return <div className="d-flex flex-column gap-2">{rows}</div>;
};

export const MailboxPick = TypedMemo(MailboxPickComponent);
